import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const AccountsDashboard = ({ onNavigate, userInfo }) => {
  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountsData, setAccountsData] = useState({
    name: "Loading...",
    accountsId: "...",
    department: "Accounts Department",
    designation: "Accounts Officer",
    profileImage: null
  });

  // Dashboard Data States
  const [dashboardStats, setDashboardStats] = useState({
    totalTransactions: 0,
    pendingPayments: 0,
    completedCertificates: 0,
    activeWorkOrders: 0,
    inventoryItems: 0,
    trackedMachines: 0
  });

  // Dashboard Content States
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [notices, setNotices] = useState([]);
  const [events, setEvents] = useState([]);

  // Fetch Dashboard Data from API
  useEffect(() => {
    const fetchAccountsDashboard = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch accounts dashboard data...');

        // Get token from storage
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        console.log('🔑 Token found:', token ? 'Yes' : 'No');

        if (!token) {
          console.log('❌ No token found, using fallback data');
          setError('Authentication required. Please login again.');
          setAccountsData(prev => ({
            ...prev,
            name: userInfo?.name || "Accounts User",
            accountsId: userInfo?.id || "ACC2024001"
          }));
          setLoading(false);
          return;
        }

        // Construct the full API URL
        const apiUrl = `${API_LINK}/api/accounts/dashboard`;
        console.log('🌐 API URL:', apiUrl);

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);

        if (!response.ok) {
          if (response.status === 401) {
            console.log('🔐 Authentication failed');
            handleLogout();
            return;
          } else if (response.status === 404) {
            console.log('🔍 API endpoint not found');
            setError('API endpoint not found.');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.log('❌ Response is not JSON:', contentType);
          setError('Server returned non-JSON response.');
          setLoading(false);
          return;
        }

        const result = await response.json();
        console.log('📦 Full API Response:', result);

        if (result.success && result.data) {
          const data = result.data;
          console.log('✅ Processing API data...');

          // Update accounts data
          setAccountsData({
            name: data.accounts?.name || userInfo?.name || "Accounts Officer",
            accountsId: data.accounts?.accountsId || userInfo?.id || "ACC2024001",
            department: data.accounts?.department || "Accounts Department",
            designation: data.accounts?.designation || "Accounts Officer",
            profileImage: data.accounts?.profileImage || null
          });

          // Update dashboard stats
          setDashboardStats({
            totalTransactions: data.stats?.totalTransactions || 0,
            pendingPayments: data.stats?.pendingPayments || 0,
            completedCertificates: data.stats?.completedCertificates || 0,
            activeWorkOrders: data.stats?.activeWorkOrders || 0,
            inventoryItems: data.stats?.inventoryItems || 0,
            trackedMachines: data.stats?.trackedMachines || 0
          });

          // Update other data
          setRecentTransactions(data.recentTransactions || []);
          setPendingTasks(data.pendingTasks || []);
          setNotices(data.notices || []);
          setEvents(data.events || []);

          console.log('✅ Accounts dashboard data updated successfully');
        } else {
          console.log('❌ API returned error:', result.message);
          setError(result.message || 'Failed to fetch dashboard data');
        }

      } catch (error) {
        console.error('💥 Error fetching accounts dashboard data:', error);
        setError(`Failed to load dashboard data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchAccountsDashboard, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Handle logout
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Accounts Dashboard...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{ fontSize: '3rem', color: '#f44336' }}>⚠️</div>
        <p style={{ color: '#666', fontSize: '1.1rem', textAlign: 'center', maxWidth: '400px' }}>
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#238548',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <Layout role="accounts"
      onNavigate={onNavigate}
      activePage="accounts-dashboard"
      pageTitle="Accounts Portal"
      accountsData={accountsData}
    >
      {/* Stats Cards */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: window.innerWidth <= 1024 ? '1fr 1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Total Transactions Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'transform 0.3s ease'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>💰</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Total Transactions</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {dashboardStats.totalTransactions > 0 ? dashboardStats.totalTransactions : '0'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            This Month
          </p>
        </div>

        {/* Pending Payments Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'transform 0.3s ease'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>⏳</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Pending Payments</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {dashboardStats.pendingPayments > 0 ? dashboardStats.pendingPayments : '0'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            Requires Attention
          </p>
        </div>

        {/* Completed Certificates Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>📜</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Completed Certificates</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {dashboardStats.completedCertificates > 0 ? dashboardStats.completedCertificates : '0'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            This Quarter
          </p>
        </div>

        {/* Active Work Orders Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>📋</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Active Work Orders</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {dashboardStats.activeWorkOrders > 0 ? dashboardStats.activeWorkOrders : '0'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            In Progress
          </p>
        </div>

        {/* Inventory Items Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>📦</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Inventory Items</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {dashboardStats.inventoryItems > 0 ? dashboardStats.inventoryItems : '0'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            Total Items
          </p>
        </div>

        {/* Tracked Machines Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>⚙️</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Tracked Machines</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {dashboardStats.trackedMachines > 0 ? dashboardStats.trackedMachines : '0'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            Active Tracking
          </p>
        </div>
      </section>

      {/* Recent Transactions & Pending Tasks */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: window.innerWidth <= 1024 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Recent Transactions */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>💰</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Recent Transactions</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {recentTransactions.length > 0 ? recentTransactions.slice(0, 5).map((transaction, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '1rem',
                transition: 'all 0.3s ease'
              }}>
                <div>
                  <div style={{ fontWeight: 'bold', color: '#333' }}>{transaction.description}</div>
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>{formatDate(transaction.date)}</div>
                </div>
                <div style={{
                  padding: '0.5rem 1rem',
                  background: transaction.type === 'credit' ? '#e8f5e9' : '#ffebee',
                  color: transaction.type === 'credit' ? '#2e7d32' : '#c62828',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}>
                  {transaction.type === 'credit' ? '+' : '-'}${transaction.amount}
                </div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                No recent transactions
              </p>
            )}
          </div>
        </div>

        {/* Pending Tasks */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>⏳</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Pending Tasks</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {pendingTasks.length > 0 ? pendingTasks.slice(0, 5).map((task, index) => (
              <div key={index} style={{
                padding: '1rem',
                borderBottom: index < pendingTasks.length - 1 ? '1px solid #ecf0f1' : 'none',
                transition: 'all 0.3s ease'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#333' }}>{task.title}</div>
                    <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                      Due: {formatDate(task.dueDate)}
                    </div>
                  </div>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    background: task.priority === 'high' ? '#ffebee' : task.priority === 'medium' ? '#fff3e0' : '#e3f2fd',
                    color: task.priority === 'high' ? '#c62828' : task.priority === 'medium' ? '#e65100' : '#1565c0',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    {task.priority}
                  </span>
                </div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                No pending tasks
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Notices & Events */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: window.innerWidth <= 1024 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Notices */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>�</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Notices</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {notices.length > 0 ? notices.slice(0, 5).map((notice, index) => (
              <div key={index} style={{
                padding: '1rem',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '0.5rem' }}>{notice.title}</div>
                <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>{notice.description}</div>
                <div style={{ color: '#999', fontSize: '0.8rem' }}>{formatDate(notice.date)}</div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                No recent notices
              </p>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>📅</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Upcoming Events</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {events.length > 0 ? events.slice(0, 5).map((event, index) => (
              <div key={index} style={{
                display: 'flex',
                gap: '1rem',
                padding: '1rem',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '1rem',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  background: '#238548',
                  color: '#ffffff',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  minWidth: '80px'
                }}>
                  <div style={{ fontWeight: 'bold' }}>{new Date(event.date).getDate()}</div>
                  <div style={{ fontSize: '0.8rem' }}>
                    {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{event.title}</h4>
                  <p style={{
                    margin: '0 0 0.5rem 0',
                    color: '#666',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <span>🕐 {event.time}</span>
                    <span>📍 {event.location}</span>
                  </p>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.75rem',
                    background: '#f4f7fa',
                    color: '#666',
                    borderRadius: '15px',
                    fontSize: '0.8rem',
                    fontWeight: 500
                  }}>
                    {event.type}
                  </span>
                </div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                No upcoming events
              </p>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AccountsDashboard;